// ============================================
// First-party page-view tracker for the Creators Portal (creator.vastavik.org)
// ============================================
// Mirrors the main-site tracker: no third-party scripts, no cookies. Records a
// single page view (page = "creators-portal") to our API, tagged with the
// ?src= platform param so tagged links are attributed in Website Traffic.
// Fire-and-forget; never affects rendering.
// ============================================

const API_URL = (process.env.REACT_APP_API_URL || 'https://api.vastavik.org').replace(/\/+$/, '');
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';
const ENDPOINT = `${API_URL}/api/${API_VERSION}/analytics/pageview`;

const VID_KEY = 'vstvk_vid';
const SID_KEY = 'vstvk_sid';
const SRC_KEY = 'vstvk_src';

function uid(): string {
    try {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return (crypto as Crypto).randomUUID();
        }
    } catch { /* ignore */ }
    return 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function getVisitorId(): string {
    try {
        let v = localStorage.getItem(VID_KEY);
        if (!v) { v = uid(); localStorage.setItem(VID_KEY, v); }
        return v;
    } catch { return uid(); }
}

function getSessionId(): string {
    try {
        let s = sessionStorage.getItem(SID_KEY);
        if (!s) { s = uid(); sessionStorage.setItem(SID_KEY, s); }
        return s;
    } catch { return uid(); }
}

// Platform tag from a tagged link (?src=..., or ?utm_source=...). Persisted for
// the session so it sticks as the visitor navigates within the portal.
function getSource(): string | null {
    try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('src') || params.get('utm_source');
        if (fromUrl) {
            const slug = fromUrl.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
            if (slug) { sessionStorage.setItem(SRC_KEY, slug); return slug; }
        }
        return sessionStorage.getItem(SRC_KEY);
    } catch { return null; }
}

let sent = false;

// Records one page view for the portal. Safe to call on every mount — it only
// fires once per load.
export function trackCreatorVisit(): void {
    if (typeof window === 'undefined' || sent) return;
    sent = true;

    const payload = {
        page: 'creators-portal',
        path: window.location.pathname + window.location.search,
        referrer: document.referrer || null,
        source: getSource(),
        visitorId: getVisitorId(),
        sessionId: getSessionId(),
        screenW: window.screen?.width || window.innerWidth || null,
        screenH: window.screen?.height || window.innerHeight || null,
        lang: navigator.language || null,
        ts: Date.now(),
    };
    const body = JSON.stringify(payload);

    try {
        const blob = new Blob([body], { type: 'text/plain' });
        if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
    } catch { /* fall through */ }

    try {
        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body,
            keepalive: true,
        }).catch(() => {});
    } catch { /* ignore */ }
}
